import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db, COLLECTIONS } from '@/lib/firebase'
import type { StoreSettings } from '@/types'
import { pickPublicStoreSettings } from '@/lib/publicSettings'
import { STORE_NAME, DEFAULT_ORDER_SUPPORT_EMAIL, DEFAULT_STORE_URL } from '@/lib/brand'

const SETTINGS_DOC_ID = 'store'
const PUBLIC_SETTINGS_DOC_ID = 'public'
const PAYMENT_SETTINGS_DOC_ID = 'payment'

export interface PaymentSettings {
  /** @deprecated Legacy — use razorpayDevKeySecret. */
  razorpayKeySecret?: string
  razorpayDevKeySecret?: string
  razorpayProdKeySecret?: string
  smtpPass?: string
  updatedAt?: string
}

const defaultSettings: Omit<StoreSettings, 'id' | 'updatedAt'> = {
  storeName: STORE_NAME,
  tagline: 'Fragrance that inspires elegance',
  email: 'contact@ooudkraft.com',
  phone: '+91 9906857273',
  whatsapp: '+91 9906857273',
  instagram: 'https://instagram.com/ooudkraft',
  address: 'Kralpora, Kupwara, Jammu and Kashmir',
  shippingCharge: 50,
  freeShippingThreshold: 1500,
  rehbarPostPickupId: '69566430855a691be1c4c8b0',
  codEnabled: true,
  codAdvanceAmount: 0,
  razorpayEnabled: true,
  razorpayEnvironment: 'development',
  razorpayKeyId: '',
  razorpayDevKeyId: '',
  razorpayProdKeyId: '',
  currency: 'INR',
  lowStockThreshold: 5,
  returnRefundPolicy: '',
  termsAndConditions: '',
  privacyPolicy: '',
  orderEmailsEnabled: false,
  smtpHost: '',
  smtpPort: 465,
  smtpUser: '',
  smtpFrom: '',
  orderEmailSubject: 'Your {{storeName}} order {{orderNumber}}',
  orderAdminEmail: '',
  orderSupportEmail: DEFAULT_ORDER_SUPPORT_EMAIL,
  storeAppUrl: DEFAULT_STORE_URL,
  launchGateEnabled: false,
  launchAt: '',
  returnWindowHours: 24,
  returnWindowMinutes: 0,
}

export async function fetchSettings(): Promise<StoreSettings> {
  const snap = await getDoc(doc(db, COLLECTIONS.settings, SETTINGS_DOC_ID))
  if (!snap.exists()) {
    return {
      id: SETTINGS_DOC_ID,
      ...defaultSettings,
      updatedAt: new Date().toISOString(),
    }
  }
  return {
    id: SETTINGS_DOC_ID,
    ...defaultSettings,
    ...snap.data(),
  } as StoreSettings
}

/** Cart page: read only the free-shipping threshold from the public settings doc. */
export async function fetchFreeShippingThreshold(): Promise<number> {
  const snap = await getDoc(
    doc(db, COLLECTIONS.settings, PUBLIC_SETTINGS_DOC_ID)
  )
  if (!snap.exists()) {
    return defaultSettings.freeShippingThreshold ?? 0
  }

  const raw = snap.data().freeShippingThreshold
  if (raw === undefined || raw === null || raw === '') {
    return defaultSettings.freeShippingThreshold ?? 0
  }

  const value = Number(raw)
  return Number.isFinite(value) && value > 0 ? value : 0
}

/** Storefront-safe settings (no admin emails, SMTP, or internal config). */
export async function fetchPublicSettings(): Promise<StoreSettings> {
  const snap = await getDoc(
    doc(db, COLLECTIONS.settings, PUBLIC_SETTINGS_DOC_ID)
  )
  if (!snap.exists()) {
    return {
      id: PUBLIC_SETTINGS_DOC_ID,
      ...defaultSettings,
      updatedAt: new Date().toISOString(),
    }
  }
  return {
    id: PUBLIC_SETTINGS_DOC_ID,
    ...defaultSettings,
    ...snap.data(),
  } as StoreSettings
}

/** Admin: copy full store settings into the public storefront document. */
export async function syncPublicStoreSettings(
  store?: Partial<StoreSettings>
): Promise<void> {
  const source = store ?? (await fetchSettings())
  const publicData = pickPublicStoreSettings(source)
  await setDoc(
    doc(db, COLLECTIONS.settings, PUBLIC_SETTINGS_DOC_ID),
    {
      ...publicData,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  )
}

export async function updateSettings(
  data: Partial<StoreSettings>
): Promise<void> {
  const { id: _id, updatedAt: _u, ...rest } = data
  if (Object.keys(rest).length === 0) return

  const now = new Date().toISOString()
  await setDoc(
    doc(db, COLLECTIONS.settings, SETTINGS_DOC_ID),
    {
      ...rest,
      updatedAt: now,
    },
    { merge: true }
  )

  const merged = {
    ...(await fetchSettings()),
    ...rest,
    updatedAt: now,
  }
  await syncPublicStoreSettings(merged)
}

export async function fetchPaymentSettings(): Promise<PaymentSettings> {
  const snap = await getDoc(
    doc(db, COLLECTIONS.settings, PAYMENT_SETTINGS_DOC_ID)
  )
  if (!snap.exists()) return {}
  return snap.data() as PaymentSettings
}

export async function updatePaymentSettings(
  data: PaymentSettings
): Promise<void> {
  await setDoc(
    doc(db, COLLECTIONS.settings, PAYMENT_SETTINGS_DOC_ID),
    {
      ...data,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  )
}

export { defaultSettings, PAYMENT_SETTINGS_DOC_ID, PUBLIC_SETTINGS_DOC_ID }
